import React, { useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import CustomCkeditor from "../../common/customeditor";
import * as promoService from "../../services/promo.services";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import {
    FAQTYPES,
    promoTypes,
    SWAL_SETTINGS,
} from "../../../../utils/Constants";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";

const PromoAdd = (props) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        {
            title: 'Promotion Management',
            url: "/admin/promotion-management/list/1",
        },
        { title: t("link_add"), url: "" },
    ];

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t("Name is required")).test("no-spaces", "label_question_error", (value) => value.trim()),
        code: Yup.string().required(t("Code is required")).test("no-spaces", "label_answer_error", (value) => value.trim()),
        discount_value: Yup.number()
            .transform((value, originalValue) =>
                originalValue === "" ? undefined : value
            )
            .required(t("Discount is required"))
            .min(1, t("Minimum value is 1"))
            .max(100, t("Max discount percent value is 100"))
            .test(
                "no-decimal",
                t("Decimal values not allowed"),
                (value) => value === undefined || Number.isInteger(value)
            ),
        expire_date: Yup.string().required(t("Expire Date is required")),
        type: Yup.string().required(t("type is required")).test("no-spaces", "label_answer_error", (value) => value.trim()),
        usageLimit: Yup.number()
        .transform((value, originalValue) =>
            originalValue === "" ? undefined : value
        )
        .required(t("limit is required"))
        .min(1, t("Minimum value is 1"))
        .max(1000, t("Max limit  value is 1000"))
        .test(
            "no-decimal",
            t("Decimal values not allowed"),
            (value) => value === undefined || Number.isInteger(value)
        )
    });
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: "",
            code: "",
            discount_value: "",
            expire_date: "",
            type: '', 
            usageLimit: 0
            
        },
        validateOnBlur: false,
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            globalLoader(true);
            setSubmitted(true);
            let formData = new FormData();
            formData.append("name", values.name);
            formData.append("code", values.code);
            formData.append("discountValue", values.discount_value);
            formData.append("expiresAt", values.expire_date);
            formData.append("type", values.type);
            formData.append("usageLimit", values.usageLimit);
            promoService.Add(formData).then((response) => {
                setSubmitting(false);
                if (response.success) {
                    Swal.fire({
                        icon: "success",
                        text: response.message,
                        ...SWAL_SETTINGS,
                    });
                    globalLoader(false);
                    resetForm({ values: "" });
                    navigate(`/admin/promotion-management/list/1`);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(response),
                        ...SWAL_SETTINGS,
                    });
                    setSubmitted(false);
                    globalLoader(false);
                }
            })
                .catch((error) => {
                    console.log("error ====> ", error);
                });
        },
    });

    return (
      <>
        <Breadcrums data={breadcrumbs} />
        <form onSubmit={formik.handleSubmit}>
          <div className="row row-sm">
            <div className="col-lg-12 col-md-12 animation_fade">
              <div className="card custom-card">
                <div className="card-body">
                  <div>
                    <h6 className="main-content-label mb-3">{t("link_add")} Promotion</h6>
                  </div>
                  <div className="row row-sm">
                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="name" className="text-left d-flex">
                        {t("Name")}
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input name="name" id="name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name} className="form-control" />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="name" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="code" className="text-left d-flex">
                        {t("Code")}
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input name="code" id="code" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.code} className="form-control" />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="code" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="discount_value" className="text-left d-flex">
                        {t("Discount")}(%)
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="discount_value"
                        id="discount_value"
                        type="number"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.discount_value}
                        className="form-control"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="discount_value" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="usageLimit" className="text-left d-flex">
                        Limit
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="usageLimit"
                        id="usageLimit"
                        type="number"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.usageLimit}
                        className="form-control"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="usageLimit" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="expire_date" className="text-left d-flex">
                        {t("Expire Date")}
                        <span className="requirestar">*</span>{" "}
                      </label>

                      <input
                        name="expire_date"
                        id="expire_date"
                        type="date"
                        min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.expire_date}
                        className="form-control"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => e.target.showPicker()}
                      />

                      <span className="text-danger d-flex text-left">
                        <CustomError name="expire_date" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="type" className="text-left d-flex">
                        Type:
                        <span className="requirestar">*</span>
                      </label>

                      <div className="select-down-arrow">
                        <select name="type" id="type" className="form-control" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.type || ""}>
                          <option value="" disabled>
                            {t("Select Type")}
                          </option>

                          {promoTypes.map((item) => (
                            <option key={item.value} value={item.value}>
                              {t(item.name)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {formik.touched.type && formik.errors.type && <span className="text-danger d-flex text-left">{formik.errors.type}</span>}
                    </div>

                    <div className="mt-5">
                      <button className="btn btn-info mr-2" type="submit" disabled={submitted ? true : null}>
                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                        {t("btn_submit")}
                      </button>
                      <button className="btn ripple btn-secondary" type="button" disabled={submitted ? true : null} onClick={() => navigate(-1)}>
                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                        {t("btn_cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </>
    );
};

export default PromoAdd;
